<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import currencyToInt from '@/helpers/currencyToInt'
import { useHotelsStore } from '@/stores/hotels'

import type { CancellationFilter } from '../helpers/hotelFilters'
import FilterChecklist from './FilterChecklist.vue'
import CheapestRateNotice from './CheapestRateNotice.vue'
import FilterPill from './FilterPill.vue'

const store = useHotelsStore()
const {
	minRatingFilter,
	minPriceFilter,
	maxPriceFilter,
	accommodationTypeFilter,
	boardFilter,
	chainFilter,
	cityFilter,
	areaFilter,
	cancellationFilter,
	accommodationTypeOptions,
	boardOptions,
	chainOptions,
	cityOptions,
	areaOptions,
	cancellationCounts,
	hotels,
} = storeToRefs(store)

const ratingOptions = [0, 3, 4, 5]

const cancellationOptions: { value: Exclude<CancellationFilter, 'all'>; label: string }[] = [
	// Las mismas palabras que el detalle de la habitación (RoomEntry).
	{ value: 'refundable', label: 'Cancelación sin costo' },
	{ value: 'non_refundable', label: 'No reembolsable' },
]

// Lo que dice cada pastilla cuando su filtro está activo. Se arma aquí y no en el
// store porque las etiquetas (moneda, rating) son de presentación.
const cancellationValue = computed(
	() => cancellationOptions.find((option) => option.value === cancellationFilter.value)?.label ?? null
)

const ratingValue = computed(() => (minRatingFilter.value > 0 ? `${minRatingFilter.value}★ o más` : null))

const priceRangeLabel = computed(
	() =>
		`${formatPrice(sliderMin.value)} – ${maxPriceFilter.value === null ? `${formatPrice(sliderMax.value)}+` : formatPrice(sliderMax.value)} ${requestedCurrency.value}`
)

const priceValue = computed(() =>
	minPriceFilter.value !== null || maxPriceFilter.value !== null ? priceRangeLabel.value : null
)

function clearPrice() {
	minPriceFilter.value = null
	maxPriceFilter.value = null
}

// dailyRateApproximate/minPriceFilter/maxPriceFilter viven en requestedCurrency
// de la búsqueda (fijo para todos los hoteles del resultado, ver stores/hotels.ts).
// El toggle de moneda de cada card es solo visual (HotelEntry.vue) y no afecta
// esto, pero el label del bucket sí debe reflejar la moneda real o confunde
// si la búsqueda no fue en MXN.
const requestedCurrency = computed(() => hotels.value[0]?.requestedCurrency || 'MXN')

// Rango de precio con slider de 2 manijas (reemplaza los chips fijos de antes,
// que no dejaban elegir un rango a la medida). El tope del slider se calcula
// del precio más alto que realmente aparece en los resultados, redondeado
// hacia arriba, para que el rango completo sea siempre útil (no un tope fijo
// arbitrario que no aplique a este destino).
const PRICE_STEP = 100
const priceDomainMax = computed(() => {
	if (!hotels.value.length) return 10000
	const max = Math.max(...hotels.value.map((h) => currencyToInt(h.dailyRateApproximate || '0')))
	return Math.max(3000, Math.ceil(max / 500) * 500)
})

const sliderMin = computed({
	get: () => minPriceFilter.value ?? 0,
	set: (value: number) => {
		const clamped = Math.min(value, sliderMax.value)
		minPriceFilter.value = clamped <= 0 ? null : clamped
	},
})

const sliderMax = computed({
	get: () => maxPriceFilter.value ?? priceDomainMax.value,
	set: (value: number) => {
		const clamped = Math.max(value, sliderMin.value)
		maxPriceFilter.value = clamped >= priceDomainMax.value ? null : clamped
	},
})

function formatPrice(value: number) {
	return new Intl.NumberFormat('es-MX').format(value)
}

// Segmented control estilo iOS, versión compacta: fondo gris, "pastilla"
// blanca flotando sobre la opción activa.
function segClass(active: boolean) {
	return [
		'flex-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[13px] font-medium transition-all duration-200',
		active
			? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.12)]'
			: 'text-gray-500 hover:text-gray-700',
	]
}

// Renglón de una lista de opción única dentro de un popover (cancelación, tipo, régimen).
function optionRowClass(active: boolean) {
	return [
		'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
		active ? 'bg-[#021F58]/[0.06] font-medium text-[#021F58]' : 'text-gray-800 hover:bg-gray-50',
	]
}

// Municipio y zona los reporta cada proveedor, no los calculamos con la ubicación:
// un hotel puede venir como «Centro» y su punto en el mapa quedar en otra parte.
const providerLocationNote =
	'Municipio y zona los informa cada proveedor y pueden no coincidir con el punto en el mapa: un hotel marcado como «Centro» puede estar en otra parte de la ciudad.'
</script>

<template>
	<!--
		Barra de pastillas: cada filtro es un botón compacto que abre su control en un
		popover, así la barra no enseña todos los controles a la vez. Envuelve sola, así
		que sirve igual en la barra ancha del listado y en el panel angosto del mapa.
	-->
	<div class="flex min-w-0 flex-wrap items-center gap-2 rounded-xl bg-white p-3 ring-1 ring-black/[0.06]">
		<!-- Cancelación: juzga la tarifa que muestra la tarjeta (la más barata). -->
		<FilterPill
			label="Cancelación"
			icon="event_available"
			:value="cancellationValue"
			content-class="w-64"
			@clear="cancellationFilter = 'all'"
		>
			<div role="radiogroup" aria-label="Cancelación" class="-mx-1 flex flex-col">
				<button
					v-for="option in cancellationOptions"
					:key="option.value"
					type="button"
					role="radio"
					:aria-checked="cancellationFilter === option.value"
					:disabled="cancellationCounts[option.value] === 0 && cancellationFilter !== option.value"
					:class="optionRowClass(cancellationFilter === option.value)"
					@click="cancellationFilter = cancellationFilter === option.value ? 'all' : option.value"
				>
					<span class="material-symbols-outlined w-4 text-[16px] leading-none" aria-hidden="true">
						{{ cancellationFilter === option.value ? 'check' : '' }}
					</span>
					<span class="flex-1">{{ option.label }}</span>
					<span class="text-[12px] tabular-nums text-gray-400">{{ cancellationCounts[option.value] }}</span>
				</button>
				<!-- Los que no entran en ninguna, para que los números cuadren con la lista. -->
				<p
					v-if="cancellationCounts.unknown > 0"
					class="flex items-center gap-2.5 px-2 py-1.5 text-[13px] text-gray-500"
				>
					<span class="w-4" aria-hidden="true"></span>
					<span class="flex-1">Sin información</span>
					<span class="text-[12px] tabular-nums text-gray-400">{{ cancellationCounts.unknown }}</span>
				</p>
			</div>
			<CheapestRateNotice />
		</FilterPill>

		<FilterPill label="Rating" icon="star" :value="ratingValue" @clear="minRatingFilter = 0">
			<div role="radiogroup" aria-label="Rating" class="flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5">
				<button
					v-for="option in ratingOptions"
					:key="`rating-${option}`"
					type="button"
					role="radio"
					:aria-checked="minRatingFilter === option"
					:class="segClass(minRatingFilter === option)"
					@click="minRatingFilter = option"
				>
					{{ option === 0 ? 'Todos' : `${option}★+` }}
				</button>
			</div>
		</FilterPill>

		<FilterPill label="Precio por noche" icon="payments" :value="priceValue" @clear="clearPrice">
			<span class="text-[13px] font-medium tabular-nums text-gray-700">{{ priceRangeLabel }}</span>
			<div class="relative flex h-5 w-full items-center">
				<div class="absolute inset-x-0 h-1 rounded-full bg-gray-200"></div>
				<div
					class="absolute h-1 rounded-full bg-[#021F58]"
					:style="{
						left: `${(sliderMin / priceDomainMax) * 100}%`,
						right: `${100 - (sliderMax / priceDomainMax) * 100}%`,
					}"
				></div>
				<input
					v-model.number="sliderMin"
					type="range"
					:min="0"
					:max="priceDomainMax"
					:step="PRICE_STEP"
					aria-label="Precio mínimo por noche"
					class="range-thumb pointer-events-none absolute inset-x-0 h-5 w-full appearance-none bg-transparent"
				/>
				<input
					v-model.number="sliderMax"
					type="range"
					:min="0"
					:max="priceDomainMax"
					:step="PRICE_STEP"
					aria-label="Precio máximo por noche"
					class="range-thumb pointer-events-none absolute inset-x-0 h-5 w-full appearance-none bg-transparent"
				/>
			</div>
		</FilterPill>

		<!-- Como Tipo y Régimen: si ningún hotel trae el dato, el filtro no se pinta. -->
		<FilterChecklist
			v-if="chainOptions.length > 0"
			v-model="chainFilter"
			label="Cadena hotelera"
			icon="domain"
			:options="chainOptions"
		/>

		<FilterChecklist
			v-if="cityOptions.length > 0"
			v-model="cityFilter"
			label="Municipio"
			icon="location_city"
			:options="cityOptions"
			:note="providerLocationNote"
		/>

		<FilterChecklist
			v-if="areaOptions.length > 0"
			v-model="areaFilter"
			label="Zona"
			icon="explore"
			:options="areaOptions"
			:note="providerLocationNote"
		/>

		<!-- Tipo de alojamiento: dinámico, solo si hay variedad en los resultados -->
		<FilterPill
			v-if="accommodationTypeOptions.length > 0"
			label="Tipo"
			icon="hotel"
			:value="accommodationTypeFilter !== 'all' ? accommodationTypeFilter : null"
			content-class="w-60"
			@clear="accommodationTypeFilter = 'all'"
		>
			<div role="radiogroup" aria-label="Tipo" class="-mx-1 flex flex-col">
				<button
					v-for="type in accommodationTypeOptions"
					:key="type"
					type="button"
					role="radio"
					:aria-checked="accommodationTypeFilter === type"
					:class="optionRowClass(accommodationTypeFilter === type)"
					@click="accommodationTypeFilter = accommodationTypeFilter === type ? 'all' : type"
				>
					<span class="material-symbols-outlined w-4 text-[16px] leading-none" aria-hidden="true">
						{{ accommodationTypeFilter === type ? 'check' : '' }}
					</span>
					{{ type }}
				</button>
			</div>
		</FilterPill>

		<!-- Régimen: dinámico, solo si hay datos de Hotelbeds -->
		<FilterPill
			v-if="boardOptions.length > 0"
			label="Régimen"
			icon="restaurant"
			:value="boardFilter !== 'all' ? boardFilter : null"
			content-class="w-60"
			@clear="boardFilter = 'all'"
		>
			<div role="radiogroup" aria-label="Régimen" class="-mx-1 flex flex-col">
				<button
					v-for="board in boardOptions"
					:key="board"
					type="button"
					role="radio"
					:aria-checked="boardFilter === board"
					:class="optionRowClass(boardFilter === board)"
					@click="boardFilter = boardFilter === board ? 'all' : board"
				>
					<span class="material-symbols-outlined w-4 text-[16px] leading-none" aria-hidden="true">
						{{ boardFilter === board ? 'check' : '' }}
					</span>
					{{ board }}
				</button>
			</div>
		</FilterPill>

	</div>
</template>

<style scoped>
/* Slider de 2 manijas: los <input type=range> quedan superpuestos con
   pointer-events-none (para no bloquearse entre sí) y solo el thumb es
   clickeable, que es el truco estándar para simular un rango de doble
   manija sin librería. */
.range-thumb::-webkit-slider-thumb {
	appearance: none;
	pointer-events: auto;
	width: 1.05rem;
	height: 1.05rem;
	border-radius: 9999px;
	background: white;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
	border: 1px solid rgba(0, 0, 0, 0.08);
	cursor: pointer;
}

.range-thumb::-moz-range-thumb {
	pointer-events: auto;
	width: 1.05rem;
	height: 1.05rem;
	border-radius: 9999px;
	background: white;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
	border: 1px solid rgba(0, 0, 0, 0.08);
	cursor: pointer;
}

.range-thumb::-webkit-slider-runnable-track {
	background: transparent;
}

.range-thumb::-moz-range-track {
	background: transparent;
}
</style>
